/**
 * LandmarkFAB - Floating Action Button for Landmark Route Creation
 * 
 * Features:
 * - Flag FAB that opens radial/fan menu of landmark icons
 * - Drag and drop landmarks onto the map
 * - First landmark = Start flag (required)
 * - Last landmark = Destination with checkered flag
 * - Auto-draws polyline connecting all landmarks
 * - Saves to caliche routes with voting
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Fab,
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  SvgIcon,
  SvgIconProps,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getCyberColors } from '../theme/cyberColors';
import { telegram } from '../services/telegram';
import { LandmarksBottomSheet } from './LandmarksBottomSheet';

// Custom turn icons with bold, rounded arrowheads for better visibility
const TurnLeftBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.22" />
    <path
      d="M38 18L22 32l16 14v-8h10V26H38z"
      fill="currentColor"
      opacity="0.95"
    />
  </SvgIcon>
);

const TurnRightBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.22" />
    <path
      d="M26 18L42 32 26 46v-8H16V26h10v-8z"
      fill="currentColor"
      opacity="0.95"
    />
  </SvgIcon>
);

const StartBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.22" />
    <path d="M24 18h4v28h-4z" fill="currentColor" opacity="0.95" />
    <path d="M28 18l16 8-16 8z" fill="currentColor" opacity="0.95" />
  </SvgIcon>
);

const DestinationBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.22" />
    <path d="M20 18h24v8H20z" fill="#fff" opacity="0.95" />
    <path d="M20 30h24v8H20z" fill="currentColor" opacity="0.95" />
    <path d="M20 42h24v4H20z" fill="#fff" opacity="0.95" />
  </SvgIcon>
);

const StraightBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.22" />
    <path d="M28 44V28h-6l10-10 10 10h-6v16z" fill="currentColor" opacity="0.95" />
  </SvgIcon>
);

const ForkLeftBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.22" />
    {/* Main stem going up */}
    <path d="M30 46V24" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.95" strokeLinecap="round" />
    {/* Left upper branch */}
    <path d="M30 24L18 18" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.95" strokeLinecap="round" />
    {/* Left lower branch */}
    <path d="M30 24L18 30" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.95" strokeLinecap="round" />
  </SvgIcon>
);

const ForkRightBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.22" />
    {/* Main stem going up */}
    <path d="M34 46V24" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.95" strokeLinecap="round" />
    {/* Right upper branch */}
    <path d="M34 24L46 18" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.95" strokeLinecap="round" />
    {/* Right lower branch */}
    <path d="M34 24L46 30" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.95" strokeLinecap="round" />
  </SvgIcon>
);

const GateBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <rect x="10" y="24" width="44" height="4" fill="currentColor" opacity="0.9" />
    <rect x="10" y="36" width="44" height="4" fill="currentColor" opacity="0.9" />
    <rect x="18" y="20" width="4" height="28" fill="currentColor" opacity="0.9" />
    <rect x="42" y="20" width="4" height="28" fill="currentColor" opacity="0.9" />
  </SvgIcon>
);

const CattleGuardBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    {[0,8,16,24,32,40,48,56].map(x => (
      <rect key={x} x={x+4} y="18" width="4" height="28" fill="currentColor" opacity="0.9" />
    ))}
  </SvgIcon>
);

const HazardBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <path d="M32 10l20 36H12L32 10z" fill="currentColor" opacity="0.9" />
    <rect x="30" y="26" width="4" height="12" fill="#fff" opacity="0.95" />
    <rect x="30" y="42" width="4" height="4" fill="#fff" opacity="0.95" />
  </SvgIcon>
);

const ParkingBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="32" r="28" fill="currentColor" opacity="0.18" />
    <rect x="20" y="16" width="8" height="32" fill="currentColor" opacity="0.9" />
    <path d="M28 16h10c6 0 10 4 10 10s-4 10-10 10H28V16z" fill="currentColor" opacity="0.9" />
    <rect x="28" y="24" width="8" height="6" fill="#fff" opacity="0.95" />
  </SvgIcon>
);

const BridgeBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <rect x="10" y="34" width="44" height="8" fill="currentColor" opacity="0.9" />
    <path d="M14 34c0-8 6-14 18-14s18 6 18 14" stroke="#fff" strokeWidth="4" fill="none" opacity="0.9" />
  </SvgIcon>
);

const FuelBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <rect x="18" y="18" width="16" height="28" rx="3" ry="3" fill="currentColor" opacity="0.9" />
    <rect x="34" y="22" width="10" height="8" rx="2" ry="2" fill="currentColor" opacity="0.9" />
    <path d="M42 30v12c0 4-3 8-7 8H22" stroke="#fff" strokeWidth="4" fill="none" opacity="0.9" />
  </SvgIcon>
);

const WaypointBadge = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 64 64" {...props}>
    <circle cx="32" cy="24" r="10" fill="currentColor" opacity="0.95" />
    <path d="M32 10c-9 0-16 7-16 16 0 12 16 28 16 28s16-16 16-28c0-9-7-16-16-16z" fill="currentColor" opacity="0.18" />
  </SvgIcon>
);

// Landmark type definitions with MUI icons and colors
export const LANDMARK_TYPES = {
  start: { 
    icon: 'Flag', 
    label: 'Start', 
    color: '#22c55e',
    description: 'Starting point of the route'
  },
  continue_straight: {
    icon: 'Straight',
    label: 'Continue Straight',
    color: '#0ea5e9',
    description: 'Continue straight ahead'
  },
  turn_left: { 
    icon: 'TurnLeft', 
    label: 'Turn Left', 
    color: '#3b82f6',
    description: 'Turn left here'
  },
  turn_right: { 
    icon: 'TurnRight', 
    label: 'Turn Right', 
    color: '#3b82f6',
    description: 'Turn right here'
  },
  fork_left: { 
    icon: 'ForkLeft', 
    label: 'Fork - Stay Left', 
    color: '#8b5cf6',
    description: 'At the fork, stay left'
  },
  fork_right: { 
    icon: 'ForkRight', 
    label: 'Fork - Stay Right', 
    color: '#8b5cf6',
    description: 'At the fork, stay right'
  },
  gate: { 
    icon: 'Gate', 
    label: 'Gate', 
    color: '#f59e0b',
    description: 'Gate - may need to open/close'
  },
  cattle_guard: { 
    icon: 'CattleGuard', 
    label: 'Cattle Guard', 
    color: '#78716c',
    description: 'Cattle guard crossing'
  },
  hazard: { 
    icon: 'Hazard', 
    label: 'Hazard', 
    color: '#ef4444',
    description: 'Caution - hazard ahead'
  },
  parking: { 
    icon: 'Parking', 
    label: 'Parking', 
    color: '#06b6d4',
    description: 'Parking area'
  },
  bridge: { 
    icon: 'Bridge', 
    label: 'Bridge/Crossing', 
    color: '#64748b',
    description: 'Bridge or water crossing'
  },
  fuel: { 
    icon: 'Fuel', 
    label: 'Fuel Stop', 
    color: '#f97316',
    description: 'Fuel available nearby'
  },
  waypoint: { 
    icon: 'Waypoint', 
    label: 'Waypoint', 
    color: '#a855f7',
    description: 'General waypoint'
  },
  destination: { 
    icon: 'Destination', 
    label: 'Destination', 
    color: '#ec4899',
    description: 'Final destination'
  },
} as const;

export type LandmarkType = keyof typeof LANDMARK_TYPES;

// SVG path data for landmark icons (simplified versions of MUI icons)
export const ICON_SVG_PATHS: Record<string, string> = {
  Flag: 'M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z',
  Straight: 'M12 4L8 8h3v8H8l4 4 4-4h-3V8h3L12 4z',
  TurnLeft: 'M11 6L5 12l6 6v-4h4v6h2V12h-6V6z',
  TurnRight: 'M13 6l6 6-6 6v-4H9v6H7V12h6V6z',
  ForkLeft: 'M14 4v6l3-3 1.4 1.4-4.4 4.4V20h-4v-7.2L5.6 8.4 7 7l3 3V4h4z',
  ForkRight: 'M10 4v6L7 7l-1.4 1.4 4.4 4.4V20h4v-7.2l4.4-4.4L17 7l-3 3V4h-4z',
  Gate: 'M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm6 9.09c0 4-2.55 7.7-6 8.83-3.45-1.13-6-4.82-6-8.83V6.31l6-2.25 6 2.25v4.78z',
  CattleGuard: 'M21 6H3v2h18V6zm0 4H3v2h18v-2zm0 4H3v2h18v-2z',
  Hazard: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
  Parking: 'M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z',
  Bridge: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
  Fuel: 'M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5z',
  Waypoint: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  Destination: 'M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6zm3.6 8h-5.6l-.4-2H7V6h5.6l.4 2H18v6z',
};

// Exported helper so map views render identical Material 3 markers
export const buildMaterialLandmarkIcon = (
  type: LandmarkType,
  opts: { isStart?: boolean; isDestination?: boolean; size?: number; offsetLat?: number; offsetLng?: number } = {}
): google.maps.Icon => {
  const config = LANDMARK_TYPES[type] || LANDMARK_TYPES.waypoint;
  const color = config.color;
  const svgPath = ICON_SVG_PATHS[config.icon];
  const size = opts.size || 56;
  const isStart = !!opts.isStart;
  const isDestination = !!opts.isDestination;

  // Center the 24px MUI path inside the 64x64 viewBox after scaling
  const iconScale = 1.6;
  const baseIconSize = 24;
  const translate = (64 - baseIconSize * iconScale) / 2;

  // Material-esque badge with surface + primary container + white glyph
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
      <defs>
        <filter id="lm_elev_${type}_${isStart ? 'start' : isDestination ? 'finish' : 'mid'}" x="-150%" y="-150%" width="400%" height="400%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="${color}55"/>
        </filter>
      </defs>
      <g filter="url(#lm_elev_${type}_${isStart ? 'start' : isDestination ? 'finish' : 'mid'})">
        ${isDestination ? `<circle cx="32" cy="32" r="30" fill="none" stroke="${color}" stroke-width="3" stroke-dasharray="6 4" opacity="0.45"/>` : ''}
        ${isStart ? `<circle cx="32" cy="32" r="30" fill="none" stroke="${color}" stroke-width="3" opacity="0.45"/>` : ''}
        <circle cx="32" cy="32" r="22" fill="${color}" opacity="0.92" />
        <circle cx="32" cy="32" r="22" stroke="#ffffff" stroke-opacity="0.15" stroke-width="2" fill="none" />
        <path d="${svgPath}" fill="#ffffff" opacity="0.98" transform="translate(${translate},${translate}) scale(${iconScale})" />
      </g>
    </svg>
  `;

  const encoded = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  return {
    url: encoded,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
    labelOrigin: new google.maps.Point(size / 2, size / 2 - 2),
  };
};

export interface Landmark {
  id: string;
  type: LandmarkType;
  lat: number;
  lng: number;
  name?: string;
  description?: string;
  photoUrl?: string;
  marker?: google.maps.Marker;
}

interface LandmarkFABProps {
  map: google.maps.Map | null;
  facilityId?: string;
  facilityName?: string;
  facilityLat?: number;
  facilityLng?: number;
  darkMode?: boolean;
  highContrast?: boolean;
  isOpen?: boolean;  // External control for panel visibility
  onOpenChange?: (isOpen: boolean) => void;  // Callback when panel state changes
  showFAB?: boolean;  // Whether to show the FAB button (false when controlled from toolbar)
  onRouteCreated?: (routeId: string) => void;
  onRouteCancelled?: () => void;
  onFirstLandmarkPlaced?: (lat: number, lng: number) => void;  // Callback when first landmark is placed (for auto-detecting nearest facility)
  onPlaceDestinationAt?: React.MutableRefObject<((lat: number, lng: number, facilityName?: string) => void) | null>;  // Ref for parent to trigger destination placement
}

export function LandmarkFAB({
  map,
  facilityId,
  facilityName,
  facilityLat,
  facilityLng,
  darkMode = true,
  highContrast = false,
  isOpen: externalIsOpen,
  onOpenChange,
  showFAB = true,
  onRouteCreated,
  onRouteCancelled: _onRouteCancelled,
  onFirstLandmarkPlaced,
  onPlaceDestinationAt,
}: LandmarkFABProps) {
  const cyber = getCyberColors(darkMode, highContrast);
  
  // State - use external isOpen if provided
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalIsOpen(value);
    }
  };
  const [isPlacingMode, setIsPlacingMode] = useState(false);
  
  // Enable placing mode when panel opens (either externally or internally)
  useEffect(() => {
    if (isOpen) {
      setIsPlacingMode(true);
    }
  }, [isOpen]);
  
  const [landmarks, setLandmarks] = useState<Landmark[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [routeDescription, setRouteDescription] = useState('');
  const [difficulty, setDifficulty] = useState<string>('moderate');
  const [surfaceType, setSurfaceType] = useState<string>('gravel');
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });
  const SNAP_RADIUS_METERS = 30; // snap destination to facility within this radius
  
  // Auto-fill route name when dialog opens
  useEffect(() => {
    console.log('[LandmarkFAB] Auto-fill check:', { showSaveDialog, routeName, facilityName, facilityId });
    if (showSaveDialog && !routeName && (facilityName || facilityId)) {
      const prefix = facilityName 
        ? `${facilityName}${facilityId ? `-${facilityId}` : ''}`
        : facilityId || '';
      console.log('[LandmarkFAB] Setting route name prefix:', prefix);
      setRouteName(prefix ? `${prefix}-` : '');
    }
  }, [showSaveDialog, routeName, facilityName, facilityId]);
  
  // Refs
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const dragGhostRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false); // Track if a real drag occurred (to prevent click after drag)
  const suppressClickRef = useRef(false); // Suppress click event after a real drag
  const dragLandmarkTypeRef = useRef<LandmarkType | null>(null);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const createMarkerIcon = useCallback((type: LandmarkType, isFirst: boolean, isLast: boolean): google.maps.Icon => {
    return buildMaterialLandmarkIcon(type, { isStart: isFirst, isDestination: isLast });
  }, []);

  // Get label for marker based on type and position
  const getMarkerLabel = useCallback((type: LandmarkType, isFirst: boolean, isLast: boolean) => {
    if (isFirst) return '🚩';
    if (isLast && type === 'destination') return '🏁';
    
    const labelMap: Record<string, string> = {
      continue_straight: '⬆️',
      turn_left: '⬅️',
      turn_right: '➡️',
      fork_left: '↖️',
      fork_right: '↗️',
      gate: '🚪',
      cattle_guard: '⚠️',
      hazard: '⚠️',
      parking: '🅿️',
      bridge: '🌉',
      fuel: '⛽',
      waypoint: '📍',
      destination: '🏁',
      start: '🚩',
    };
    return labelMap[type] || '📍';
  }, []);

  // Update polyline when landmarks change
  const updatePolyline = useCallback(() => {
    if (!map) return;
    
    // Remove existing polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
    }
    
    if (landmarks.length < 2) return;
    
    // Create path from landmarks
    const path = landmarks.map(lm => ({ lat: lm.lat, lng: lm.lng }));
    
    // Create new polyline with cyberpunk style
    polylineRef.current = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: cyber.neonCyan,
      strokeOpacity: 0.9,
      strokeWeight: 4,
      map,
      icons: [{
        icon: {
          path: google.maps.SymbolPath.FORWARD_OPEN_ARROW,
          scale: 3,
          strokeColor: cyber.neonCyan,
        },
        offset: '50%',
      }],
    });
  }, [map, landmarks, cyber]);

  // Update marker icons to reflect position (first/last)
  const updateMarkerIcons = useCallback(() => {
    landmarks.forEach((lm, index) => {
      if (lm.marker && lm.marker.getMap()) { // Only update markers still on the map
        const isFirst = index === 0;
        const isLast = index === landmarks.length - 1;
        const newIcon = createMarkerIcon(lm.type, isFirst, isLast);
        lm.marker.setIcon(newIcon);
        // Update label as well
        lm.marker.setLabel({
          text: getMarkerLabel(lm.type, isFirst, isLast),
          color: '#ffffff',
          fontSize: '18px',
          fontWeight: 'bold',
        });
      }
    });
  }, [landmarks, createMarkerIcon, getMarkerLabel]);

  // Handle map click to place landmark
  // (Now handled via drag-drop from LandmarksBottomSheet instead)

  // Place a landmark at specific coordinates (used by both click and drag)
  const placeLandmarkAt = useCallback((landmarkType: LandmarkType, lat: number, lng: number) => {
    if (!map) return;
    
    console.log('[LandmarkFAB] Placing landmark:', { landmarkType, lat, lng, isFirst: landmarks.length === 0 });
    
    // Create landmark
    const newLandmark: Landmark = {
      id: crypto.randomUUID(),
      type: landmarkType,
      lat,
      lng,
    };
    
    // Create marker
    const isFirst = landmarks.length === 0;
    const isDestination = landmarkType === 'destination';
    
    const markerIcon = createMarkerIcon(landmarkType, isFirst, isDestination);
    console.log('[LandmarkFAB] Created marker icon:', markerIcon);
    
    const marker = new google.maps.Marker({
      position: { lat, lng },
      map,
      icon: markerIcon,
      label: {
        text: getMarkerLabel(landmarkType, isFirst, isDestination),
        color: '#ffffff',
        fontSize: '24px',
        fontWeight: 'bold',
      },
      draggable: true,
      animation: google.maps.Animation.DROP,
      title: LANDMARK_TYPES[landmarkType].label,
      visible: true,
      zIndex: 999999,
      optimized: false,
      clickable: true,
    });
    
    console.log('[LandmarkFAB] Created marker:', marker, 'visible:', marker.getVisible(), 'map:', marker.getMap());
    
    // Handle marker drag
    marker.addListener('dragend', () => {
      const pos = marker.getPosition();
      if (pos) {
        setLandmarks(prev => prev.map(lm => 
          lm.id === newLandmark.id ? { ...lm, lat: pos.lat(), lng: pos.lng() } : lm
        ));
      }
    });
    
    // Handle marker click to delete
    marker.addListener('click', () => {
      if (isPlacingMode) {
        marker.setMap(null);
        setLandmarks(prev => prev.filter(lm => lm.id !== newLandmark.id));
        telegram.hapticFeedback('light');
      }
    });
    
    newLandmark.marker = marker;
    markersRef.current.push(marker);
    
    setLandmarks(prev => [...prev, newLandmark]);
    telegram.hapticFeedback('medium');
    
    // If this is the first landmark, notify parent so it can find nearest facility
    if (isFirst && onFirstLandmarkPlaced) {
      onFirstLandmarkPlaced(lat, lng);
    }
    
    // Show toast
    setToast({ 
      open: true, 
      message: `${LANDMARK_TYPES[landmarkType].label} placed! Tap marker to remove.`,
      severity: 'success'
    });
  }, [map, landmarks, createMarkerIcon, getMarkerLabel, isPlacingMode, onFirstLandmarkPlaced]);

  // Place landmark at map center


  // Expose destination placement function to parent via ref
  useEffect(() => {
    if (onPlaceDestinationAt) {
      onPlaceDestinationAt.current = (lat: number, lng: number, facilityName?: string) => {
        // Only place if in placing mode and don't already have a destination
        if (isPlacingMode && !landmarks.some(lm => lm.type === 'destination')) {
          placeLandmarkAt('destination', lat, lng);
          if (facilityName) {
            setRouteName(prev => prev || `${facilityName}-`);
          }
        }
      };
    }
    return () => {
      if (onPlaceDestinationAt) {
        onPlaceDestinationAt.current = null;
      }
    };
  }, [isPlacingMode, landmarks, placeLandmarkAt, onPlaceDestinationAt]);

  // Cleanup function to remove ghost element and reset drag state
  const cleanupDrag = useCallback(() => {
    isDraggingRef.current = false;
    didDragRef.current = false;
    dragStartPosRef.current = null;
    dragLandmarkTypeRef.current = null;
    
    // Remove ghost element if it exists
    if (dragGhostRef.current) {
      dragGhostRef.current.remove();
      dragGhostRef.current = null;
    }
    
    // Also clean up any orphaned ghost elements by ID
    const orphanedGhost = document.getElementById('landmark-drag-ghost');
    if (orphanedGhost) {
      orphanedGhost.remove();
    }
  }, []);

  // Global pointer move handler (attached to document during drag)
  const handleGlobalPointerMove = useCallback((e: PointerEvent) => {
    if (!isDraggingRef.current || !dragGhostRef.current) return;
    
    // Check if pointer moved significantly (more than 10px) to count as a real drag
    if (dragStartPosRef.current) {
      const dx = e.clientX - dragStartPosRef.current.x;
      const dy = e.clientY - dragStartPosRef.current.y;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        didDragRef.current = true;
      }
    }
    
    // Update ghost position
    dragGhostRef.current.style.left = `${e.clientX}px`;
    dragGhostRef.current.style.top = `${e.clientY}px`;
  }, []);

  // Global pointer up handler (attached to document during drag)
  const handleGlobalPointerUp = useCallback((e: PointerEvent) => {
    const wasDragging = isDraggingRef.current;
    const wasRealDrag = didDragRef.current;
    const landmarkType = dragLandmarkTypeRef.current;
    
    // Remove global listeners first
    document.removeEventListener('pointermove', handleGlobalPointerMove);
    document.removeEventListener('pointerup', handleGlobalPointerUp);
    document.removeEventListener('pointercancel', handleGlobalPointerUp);
    
    // Clean up drag state
    cleanupDrag();
    
    if (!wasDragging || !landmarkType || !map) {
      return;
    }
    
    // Only place landmark if this was a real drag (not just a tap)
    if (!wasRealDrag) {
      // This was a tap, not a drag - let onClick handle it
      return;
    }
    
    // Get the map container's bounds
    const mapContainer = map.getDiv();
    const mapRect = mapContainer.getBoundingClientRect();
    
    // Check if drop is within map bounds
    const dropX = e.clientX;
    const dropY = e.clientY;
    
    if (dropX >= mapRect.left && dropX <= mapRect.right &&
        dropY >= mapRect.top && dropY <= mapRect.bottom) {
      // Convert screen coordinates to map coordinates
      const projection = map.getProjection();
      const bounds = map.getBounds();
      
      if (projection && bounds) {
        // Calculate the position relative to the map container
        const relativeX = dropX - mapRect.left;
        const relativeY = dropY - mapRect.top;
        
        // Calculate lat/lng based on the map bounds and container size
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        
        const lngRange = ne.lng() - sw.lng();
        const latRange = ne.lat() - sw.lat();
        
        const lng = sw.lng() + (relativeX / mapRect.width) * lngRange;
        const lat = ne.lat() - (relativeY / mapRect.height) * latRange;
        
        // Place the landmark at the drop location
        placeLandmarkAt(landmarkType, lat, lng);
        
        // Suppress the onClick that will fire after this
        suppressClickRef.current = true;
        setTimeout(() => { suppressClickRef.current = false; }, 100);
      }
    } else {
      // Dropped outside map - show warning
      setToast({ 
        open: true, 
        message: 'Drop the landmark on the map!',
        severity: 'info'
      });
      telegram.hapticFeedback('warning');
      
      // Suppress the onClick since we already handled this drop
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 100);
    }
  }, [map, placeLandmarkAt, cleanupDrag, handleGlobalPointerMove]);

  // Handle drag start from landmark icon
  const handleDragStart = useCallback((e: React.PointerEvent, landmarkType: LandmarkType) => {
    e.preventDefault();
    
    // Clean up any previous orphaned ghost elements first
    const existingGhost = document.getElementById('landmark-drag-ghost');
    if (existingGhost) {
      existingGhost.remove();
    }
    
    isDraggingRef.current = true;
    didDragRef.current = false; // Reset - will set to true if pointer moves significantly
    dragLandmarkTypeRef.current = landmarkType;
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    
    // Create ghost element with SVG icon
    const config = LANDMARK_TYPES[landmarkType];
    const iconPath = ICON_SVG_PATHS[config.icon] || ICON_SVG_PATHS.Waypoint;
    const ghost = document.createElement('div');
    ghost.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 9999;
      width: 48px;
      height: 48px;
      transform: translate(-50%, -50%);
      filter: drop-shadow(0 4px 8px rgba(0,0,0,0.4));
      transition: transform 0.1s;
    `;
    ghost.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="22" fill="${config.color}" stroke="white" stroke-width="3"/>
        <g transform="translate(12, 12)">
          <path d="${iconPath}" fill="white"/>
        </g>
      </svg>
    `;
    ghost.id = 'landmark-drag-ghost';
    document.body.appendChild(ghost);
    dragGhostRef.current = ghost;
    
    // Position ghost at pointer
    ghost.style.left = `${e.clientX}px`;
    ghost.style.top = `${e.clientY}px`;
    
    telegram.hapticFeedback('light');
    
    // Add global listeners to track pointer across the entire document
    document.addEventListener('pointermove', handleGlobalPointerMove);
    document.addEventListener('pointerup', handleGlobalPointerUp);
    document.addEventListener('pointercancel', handleGlobalPointerUp);
  }, [handleGlobalPointerMove, handleGlobalPointerUp]);

  // Update polyline when landmarks change
  useEffect(() => {
    updatePolyline();
    updateMarkerIcons();
  }, [landmarks, updatePolyline, updateMarkerIcons]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
      }
      markersRef.current.forEach(m => m.setMap(null));
      
      // Clean up any orphaned ghost elements
      const orphanedGhost = document.getElementById('landmark-drag-ghost');
      if (orphanedGhost) {
        orphanedGhost.remove();
      }
      
      // Remove any global listeners that might still be attached
      document.removeEventListener('pointermove', handleGlobalPointerMove);
      document.removeEventListener('pointerup', handleGlobalPointerUp);
      document.removeEventListener('pointercancel', handleGlobalPointerUp);
    };
  }, [handleGlobalPointerMove, handleGlobalPointerUp]);

  // Handle undo last landmark


  // Handle save route
  const handleSaveRoute = async () => {
    if (!routeName.trim() || landmarks.length < 2) return;
    
    // Validate first is start, last is destination
    if (landmarks[0].type !== 'start') {
      setToast({ open: true, message: 'First landmark must be Start 🚩', severity: 'error' });
      return;
    }
    
    if (landmarks[landmarks.length - 1].type !== 'destination') {
      setToast({ open: true, message: 'Last landmark must be Destination 🏁', severity: 'error' });
      return;
    }
    
    setIsSaving(true);
    
    try {
      let adjustedLandmarks = landmarks;

      // Snap destination to facility coordinates if provided
      if (facilityLat !== undefined && facilityLng !== undefined) {
        const destination = landmarks[landmarks.length - 1];
        const toRad = (x: number) => x * Math.PI / 180;
        const haversineMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
          const R = 6371000; // meters
          const dLat = toRad(lat2 - lat1);
          const dLon = toRad(lon2 - lon1);
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          return R * c;
        };
        const dist = haversineMeters(destination.lat, destination.lng, facilityLat, facilityLng);
        if (dist > SNAP_RADIUS_METERS) {
          const snapped = { ...destination, lat: facilityLat, lng: facilityLng };
          adjustedLandmarks = landmarks.map((lm, idx) => idx === landmarks.length - 1 ? snapped : lm);
          // Move marker if present
          if (destination.marker) {
            destination.marker.setPosition({ lat: facilityLat, lng: facilityLng });
          }
          // Rebuild polyline path to include snapped destination
          if (polylineRef.current) {
            const path = adjustedLandmarks.map(lm => new google.maps.LatLng(lm.lat, lm.lng));
            polylineRef.current.setPath(path);
          }
          setLandmarks(adjustedLandmarks);
          setToast({ open: true, message: '🏁 Destination snapped to facility for accuracy', severity: 'info' });
        }
      }

      // Calculate total route distance using Haversine formula
      const calculateDistanceMiles = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const R = 3959; // Earth's radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
      };
      
      let totalDistanceMiles = 0;
      for (let i = 0; i < adjustedLandmarks.length - 1; i++) {
        totalDistanceMiles += calculateDistanceMiles(
          adjustedLandmarks[i].lat, adjustedLandmarks[i].lng,
          adjustedLandmarks[i + 1].lat, adjustedLandmarks[i + 1].lng
        );
      }
      const distanceStr = totalDistanceMiles < 0.1 
        ? `${Math.round(totalDistanceMiles * 5280)} ft` 
        : `${totalDistanceMiles.toFixed(1)} mi`;
      
      // Extract polyline coordinates
      const polylineCoordinates = polylineRef.current?.getPath().getArray().map(p => ({
        lat: p.lat(),
        lng: p.lng()
      })) || adjustedLandmarks.map(lm => ({ lat: lm.lat, lng: lm.lng }));
      
      const requestBody = {
        facilityId: facilityId || 'unknown',
        name: routeName.trim(),
        description: routeDescription.trim() || null,
        landmarks: adjustedLandmarks.map(lm => ({
          type: lm.type,
          lat: lm.lat,
          lng: lm.lng,
          name: lm.name,
          description: lm.description,
        })),
        polylineCoordinates,
        difficulty,
        surfaceType,
        userId: telegram.getUserId() || 'anonymous',
        username: telegram.getUserName() || null,
        isPublic: true, // Changed to true so routes appear in the list
      };
      
      console.log('[LandmarkFAB] Saving route:', requestBody);
      
      const response = await fetch('/api/landmark-routes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      console.log('[LandmarkFAB] Save response status:', response.status);
      
      const data = await response.json();
      console.log('[LandmarkFAB] Save response data:', data);
      
      if (data.success) {
        telegram.hapticFeedback('success');
        setToast({ open: true, message: `Route "${routeName}" saved! ${distanceStr} of caliche road 🛤️`, severity: 'success' });
        setShowSaveDialog(false);
        setRouteName('');
        setRouteDescription('');
        
        // Change polyline color to green to indicate saved
        if (polylineRef.current) {
          polylineRef.current.setOptions({ strokeColor: '#22c55e' });
        }
        
        onRouteCreated?.(data.routeId);
        
        // Keep markers visible on map, just close the panel
        setIsOpen(false);
        setIsPlacingMode(false);
        
        // Auto-open Google Maps with ALL landmarks as waypoints!
        // This is the BREAD AND BUTTER - Google Maps routes through each landmark point
        // even on unmapped caliche roads using GPS coordinates
        // destination = last landmark (the well/destination)
        // waypoints = all landmarks in between (the turns on caliche)
        if (adjustedLandmarks.length >= 2) {
          const destination = adjustedLandmarks[adjustedLandmarks.length - 1]; // Last landmark is destination
          const waypoints = adjustedLandmarks.slice(0, -1); // All landmarks except last become waypoints
          
          // Google Maps supports max 9 waypoints in URL - simplify if needed
          const MAX_WAYPOINTS = 9;
          let routeWaypoints = waypoints;
          
          if (waypoints.length > MAX_WAYPOINTS) {
            // Keep most important waypoints (first, last of waypoints, and evenly spaced)
            const step = Math.ceil(waypoints.length / MAX_WAYPOINTS);
            routeWaypoints = waypoints.filter((_, i) => i === 0 || i === waypoints.length - 1 || i % step === 0).slice(0, MAX_WAYPOINTS);
            console.log(`[LandmarkFAB] Simplified ${waypoints.length} waypoints to ${routeWaypoints.length}`);
          }
          
          // Format waypoints as pipe-separated coordinates
          const waypointStr = routeWaypoints
            .map(lm => `${lm.lat},${lm.lng}`)
            .join('|');
          
          // Short delay so user sees the success message
          setTimeout(() => {
            const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}&waypoints=${waypointStr}&travelmode=driving&dir_action=navigate`;
            console.log('[LandmarkFAB] 🗺️ Opening Google Maps with ALL waypoints:', mapsUrl);
            telegram.webApp.openLink(mapsUrl, { try_instant_view: false });
          }, 1500);
        }
      } else {
        throw new Error(data.error || 'Failed to save route');
      }
    } catch (error) {
      console.error('Error saving route:', error);
      telegram.hapticFeedback('error');
      setToast({ 
        open: true, 
        message: `Error: ${error instanceof Error ? error.message : 'Failed to save'}`,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Check if we can finish (has start and destination)
  // Get landmark types for display (excluding start if already placed, destination if already placed)
  const hasStart = landmarks.some(lm => lm.type === 'start');
  const hasDestination = landmarks.some(lm => lm.type === 'destination');
  
  const availableLandmarks = Object.entries(LANDMARK_TYPES).filter(([key]) => {
    if (key === 'start' && hasStart) return false;
    if (key === 'destination' && hasDestination) return false;
    return true;
  });

  return (
    <>
      {/* Main FAB - only show if showFAB is true */}
      {showFAB && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 160,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Tooltip title={isOpen ? 'Close landmark menu' : 'Create checkpoints and navigate deep into the oil field using Caliche roads'} placement="left">
            <Fab
              onClick={() => {
                // Clean up any orphaned ghost elements when closing
                if (isOpen) {
                  cleanupDrag();
                }
                setIsOpen(!isOpen);
                if (!isOpen) {
                  setIsPlacingMode(true);
                }
                telegram.hapticFeedback('light');
              }}
              sx={{
                width: 56,
              height: 56,
              backgroundColor: 'rgba(15, 23, 42, 0.95)',
              border: `3px solid ${isOpen ? cyber.neonCyan : cyber.neonCyan}`,
              color: isOpen ? cyber.neonCyan : cyber.neonCyan,
              boxShadow: `0 0 20px ${cyber.neonCyan}40, 0 0 40px ${cyber.neonCyan}20`,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(15, 23, 42, 1)',
                boxShadow: `0 0 30px ${cyber.neonCyan}60, 0 0 60px ${cyber.neonCyan}30`,
                transform: 'scale(1.05)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {isOpen ? <CloseIcon sx={{ fontSize: 36 }} /> : <FlagIcon sx={{ fontSize: 36 }} />}
            </Fab>
          </Tooltip>
        </Box>
      )}

      {/* Landmark Selection Bottom Sheet - M3 Themed */}
      <LandmarksBottomSheet
        open={isOpen}
        onClose={() => {
          cleanupDrag();
          setIsOpen(false);
        }}
        landmarks={availableLandmarks.map(([key, config]) => {
          const IconComponent = key === 'start' ? StartBadge
            : key === 'continue_straight' ? StraightBadge
            : key === 'turn_left' ? TurnLeftBadge
            : key === 'turn_right' ? TurnRightBadge
            : key === 'fork_left' ? ForkLeftBadge
            : key === 'fork_right' ? ForkRightBadge
            : key === 'gate' ? GateBadge
            : key === 'cattle_guard' ? CattleGuardBadge
            : key === 'hazard' ? HazardBadge
            : key === 'parking' ? ParkingBadge
            : key === 'bridge' ? BridgeBadge
            : key === 'fuel' ? FuelBadge
            : key === 'destination' ? DestinationBadge
            : WaypointBadge;
          
          return {
            id: key,
            label: config.label,
            icon: <IconComponent sx={{ fontSize: 40, color: config.color }} />,
            ariaLabel: `${config.label}: ${config.description}`,
            color: config.color,
          };
        })}
        onDragStart={(landmarkId, event) => {
          handleDragStart(event as any, landmarkId as LandmarkType);
        }}
        onDragEnd={() => {
          // Notify bottom sheet that drag ended (no event parameter needed)
          setIsPlacingMode(false);
        }}
        darkMode={darkMode}
      />

      {/* Save Route Dialog */}
      <Dialog
        open={showSaveDialog}
        onClose={() => !isSaving && setShowSaveDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgSecondary,
            border: `2px solid ${cyber.neonGreen}40`,
            borderRadius: 3,
            minWidth: 380,
          }
        }}
      >
        <DialogTitle sx={{ color: cyber.neonGreen, fontWeight: 700 }}>
          💾 Save Caliche Route
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: cyber.textMuted, mb: 2 }}>
            This route will be saved for <strong style={{ color: cyber.neonCyan }}>{facilityName || facilityId}</strong> and visible to all users.
          </Typography>
          
          <TextField
            autoFocus
            fullWidth
            label="Route Name"
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            placeholder="e.g., Main Access from Highway 285"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Description (Optional)"
            value={routeDescription}
            onChange={(e) => setRouteDescription(e.target.value)}
            multiline
            rows={2}
            placeholder="e.g., Best route in wet weather..."
            sx={{ mb: 2 }}
          />
          
          <Typography variant="caption" sx={{ color: cyber.textMuted, mb: 1, display: 'block' }}>
            Difficulty:
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={difficulty}
            onChange={(_, v) => v && setDifficulty(v)}
            size="small"
            sx={{ mb: 2, width: '100%' }}
          >
            <ToggleButton value="easy" sx={{ flex: 1 }}>Easy</ToggleButton>
            <ToggleButton value="moderate" sx={{ flex: 1 }}>Moderate</ToggleButton>
            <ToggleButton value="difficult" sx={{ flex: 1 }}>Difficult</ToggleButton>
          </ToggleButtonGroup>
          
          <Typography variant="caption" sx={{ color: cyber.textMuted, mb: 1, display: 'block' }}>
            Surface:
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={surfaceType}
            onChange={(_, v) => v && setSurfaceType(v)}
            size="small"
            sx={{ width: '100%' }}
          >
            <ToggleButton value="paved" sx={{ flex: 1 }}>Paved</ToggleButton>
            <ToggleButton value="gravel" sx={{ flex: 1 }}>Caliche</ToggleButton>
            <ToggleButton value="dirt" sx={{ flex: 1 }}>Dirt</ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowSaveDialog(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRoute}
            disabled={!routeName.trim() || isSaving}
            sx={{ backgroundColor: cyber.neonGreen, color: '#000' }}
          >
            {isSaving ? 'Saving...' : 'Save Route'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
